export interface SEOConfig {
  path: string;
  title: string;
  description: string;
  focusKeywords: string;
  noindex?: boolean;
  schemas: any[];
}

// Homepage Schema Generators
const sitelinkSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "Smart Imagify",
  "url": "https://smartimagify.com",
  "potentialAction": {
    "@type": "SearchAction",
    "target": "https://smartimagify.com/stock-library?q={search_term_string}",
    "query-input": "required name=search_term_string"
  }
};

const orgSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Smart Imagify",
  "url": "https://smartimagify.com",
  "logo": "https://smartimagify.com/Logo/Favicon.png",
  "description": "Free online image compression, AI generation, and editing tools."
};

// Tool page helper generators
function getToolSchema(name: string, url: string, desc: string) {
  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": name,
    "applicationCategory": "MultimediaApplication",
    "operatingSystem": "Web Browser",
    "url": url,
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "description": desc,
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "ratingCount": "1240"
    }
  };
}

function getBreadcrumbSchema(name: string, path: string) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://smartimagify.com" },
      { "@type": "ListItem", "position": 2, "name": name, "item": `https://smartimagify.com${path}` }
    ]
  };
}

// Complete Meta Data Map for All Routes
export const SEO_MAP: Record<string, SEOConfig> = {
  '/': {
    path: '/',
    title: 'Smart Imagify | All-in-One AI Image Compressor & Studio',
    description: 'Compress, generate, resize, and convert images locally. Private AI image studio and batch compression for designers and developers.',
    focusKeywords: 'image compressor, ai image generator, webp converter',
    schemas: [
      sitelinkSchema,
      orgSchema,
      getToolSchema('Smart Imagify Studio', 'https://smartimagify.com/', 'All-in-one local client browser image optimizer and compressor suite.')
    ]
  },
  '/compress-image': {
    path: '/compress-image',
    title: 'Compress Image Online | Private Lossless Compression',
    description: 'Compress JPG, PNG, and WebP images locally in your browser. Fast, free, private image size reducer under 50KB for forms and web.',
    focusKeywords: 'compress image online, reduce image size, compress jpg',
    schemas: [
      getToolSchema('Image Compressor - Smart Imagify', 'https://smartimagify.com/compress-image', 'Compress JPG, PNG, WebP images locally in your browser.'),
      getBreadcrumbSchema('Compress Image', '/compress-image'),
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [
          { "@type": "Question", "name": "How to compress image under 50KB?", "acceptedAnswer": { "@type": "Answer", "text": "Upload your image to Smart Imagify, adjust the quality slider until the estimated output size is below 50KB, then download the compressed result. All processing is done locally in your browser." }},
          { "@type": "Question", "name": "Is image compression free?", "acceptedAnswer": { "@type": "Answer", "text": "Yes, Smart Imagify offers completely free image compression with no file size limits, no watermarks, and no account required for basic use." }},
          { "@type": "Question", "name": "Does compressing reduce image quality?", "acceptedAnswer": { "@type": "Answer", "text": "Smart Imagify uses intelligent algorithms that remove redundant data while preserving visual fidelity. Most images can be compressed 60-80% with no visible quality loss." }}
        ]
      }
    ]
  },
  '/bulk-image-compressor': {
    path: '/bulk-image-compressor',
    title: 'Bulk Image Compressor | Batch Image Size Optimizer',
    description: 'Batch compress images in parallel. Convert hundreds of photos to WebP or AVIF formats. Perfect for developers and WordPress sites.',
    focusKeywords: 'bulk image compressor, batch compress images, wordpress image optimizer',
    schemas: [
      getToolSchema('Bulk Image Compressor - Smart Imagify', 'https://smartimagify.com/bulk-image-compressor', 'Batch compress images in parallel to WebP/AVIF.'),
      getBreadcrumbSchema('Bulk Image Compressor', '/bulk-image-compressor'),
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [
          { "@type": "Question", "name": "How to compress images for WordPress?", "acceptedAnswer": { "@type": "Answer", "text": "Upload your WordPress media library images to Smart Imagify's Bulk Compressor. Select WebP output format, set quality to 80%, and download the optimized ZIP. Upload the WebP files to your WordPress media folder for instant PageSpeed improvements." }},
          { "@type": "Question", "name": "Can I compress hundreds of images at once?", "acceptedAnswer": { "@type": "Answer", "text": "Yes, Smart Imagify's bulk compressor processes hundreds of images in parallel using your browser's multi-threaded capabilities. All files are compressed locally and downloaded as a single ZIP archive." }}
        ]
      }
    ]
  },
  '/ai-image-generator': {
    path: '/ai-image-generator',
    title: 'AI Image Generator | Free Text to Art Studio Online',
    description: 'Generate high-quality AI images locally from text prompts. Create beautiful, royalty-free art with premium seed dream models.',
    focusKeywords: 'ai image generator free, text to image, ai art generator',
    schemas: [
      getToolSchema('AI Image Generator - Smart Imagify', 'https://smartimagify.com/ai-image-generator', 'Generate high quality AI images locally from text prompts.'),
      getBreadcrumbSchema('AI Image Generator', '/ai-image-generator'),
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [
          { "@type": "Question", "name": "Is the AI image generator free?", "acceptedAnswer": { "@type": "Answer", "text": "Yes, Smart Imagify's AI Image Generator is completely free. Generate unlimited royalty-free images from text prompts with no hidden costs or watermarks." }},
          { "@type": "Question", "name": "What AI models are used?", "acceptedAnswer": { "@type": "Answer", "text": "Smart Imagify uses premium seed dream models optimized for high-quality art generation. Results are royalty-free and can be used commercially." }}
        ]
      }
    ]
  },
  '/image-resizer': {
    path: '/image-resizer',
    title: 'Image Resizer Online | Crop & Scale Dimensions Free',
    description: 'Change image width and height online. Crop, scale, and adjust aspect ratios of images privately in your browser without quality loss.',
    focusKeywords: 'image resizer online, resize image online, crop image online',
    schemas: [
      getToolSchema('Image Resizer - Smart Imagify', 'https://smartimagify.com/image-resizer', 'Change image dimensions, crop, and scale online locally.'),
      getBreadcrumbSchema('Image Resizer', '/image-resizer')
    ]
  },
  '/image-format-converter': {
    path: '/image-format-converter',
    title: 'Image Format Converter | Convert PNG, JPG, WebP, AVIF Free',
    description: 'Convert photos and images between PNG, WebP, AVIF, JPG, BMP, and TIFF formats. High fidelity local format conversions maintaining transparency.',
    focusKeywords: 'image format converter, image extension converter, convert png to jpg, convert image to webp, convert jpg to png',
    schemas: [
      getToolSchema('Image Format Converter - Smart Imagify', 'https://smartimagify.com/image-format-converter', 'Convert photos and images between PNG, WebP, AVIF, JPG, BMP, and TIFF formats.'),
      getBreadcrumbSchema('Image Format Converter', '/image-format-converter')
    ]
  },
  '/collage-maker': {
    path: '/collage-maker',
    title: 'Collage Maker Online | Create Free Photo Collages',
    description: 'Create beautiful photo collages online for free. Choose from multiple layout templates, customize margins, border radius, background colors, and export high quality images locally.',
    focusKeywords: 'collage maker, photo collage online, collage creator free',
    schemas: [
      getToolSchema('Collage Maker - Smart Imagify', 'https://smartimagify.com/collage-maker', 'Create beautiful photo collages with templates locally in your browser.'),
      getBreadcrumbSchema('Collage Maker', '/collage-maker')
    ]
  },
  '/passport-photo-maker': {
    path: '/passport-photo-maker',
    title: 'Passport Photo Maker Online | Free Biometric ID Photo Tool',
    description: 'Create official passport, visa, and ID photos online for free. Automatic biometric face cropping, background removal, and standard country size templates (2x2 inch, 35x45mm). 100% private local processing.',
    focusKeywords: 'passport photo maker online, biometric photo maker, visa photo size online, id photo maker free, 2x2 photo maker',
    schemas: [
      getToolSchema('Passport Photo Maker - Smart Imagify', 'https://smartimagify.com/passport-photo-maker', 'Create official passport, visa, and ID photos with biometric cropping locally in your browser.'),
      getBreadcrumbSchema('Passport Photo Maker', '/passport-photo-maker'),
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [
          { "@type": "Question", "name": "How to make a 2x2 inch passport photo online?", "acceptedAnswer": { "@type": "Answer", "text": "Upload your portrait to Smart Imagify's Passport Photo Maker. Select the US 2x2 inch template (or any international visa size), align your face using biometric guides, and download your printable photo grid locally for free." }},
          { "@type": "Question", "name": "Is the passport photo maker private?", "acceptedAnswer": { "@type": "Answer", "text": "Yes! All biometric facial cropping, resizing, and photo processing run 100% locally in your web browser. Your sensitive ID photos are never uploaded to any cloud server." }}
        ]
      }
    ]
  },
  '/bulk-image-optimizer': {
    path: '/bulk-image-compressor',
    title: 'Bulk Image Compressor | Batch Image Size Optimizer',
    description: 'Batch compress images in parallel. Convert hundreds of photos to WebP or AVIF formats. Perfect for developers and WordPress sites.',
    focusKeywords: 'bulk image compressor, batch compress images, wordpress image optimizer',
    schemas: [
      getToolSchema('Bulk Image Compressor - Smart Imagify', 'https://smartimagify.com/bulk-image-compressor', 'Batch compress images in parallel to WebP/AVIF.'),
      getBreadcrumbSchema('Bulk Image Compressor', '/bulk-image-compressor')
    ]
  },
  '/about-us': {
    path: '/about',
    title: 'About Us | Smart Imagify - Ayshdan Innovations',
    description: 'Discover the story behind Smart Imagify. Created by Muhammad Adnan under AYSHDAN INNOVATIONS to provide high fidelity image utility tools.',
    focusKeywords: 'about, ayshdan innovations, muhammad adnan',
    schemas: [
      { "@type": "WebPage", "name": "About Smart Imagify" },
      getBreadcrumbSchema('About Us', '/about')
    ]
  },
  '/contact-us': {
    path: '/contact',
    title: 'Contact Us | Smart Imagify Support Desk',
    description: 'Get in touch with Muhammad Adnan and the AYSHDAN INNOVATIONS tech support team for queries relative to local SDK, corporate plans, or help.',
    focusKeywords: 'contact support, smart imagify help desk',
    schemas: [
      { "@type": "WebPage", "name": "Contact Support" },
      getBreadcrumbSchema('Contact Us', '/contact')
    ]
  },
  '/stock-library': {
    path: '/stock-library',
    title: 'Free Stock Photos | High-Resolution Royalty Free Library',
    description: 'Search and download millions of free high-resolution stock images. Perfect for WordPress design, developers, and blog posts.',
    focusKeywords: 'free stock photos, royalty free images, free high resolution photos',
    schemas: [
      {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": "Free Stock Photos Library - Smart Imagify",
        "description": "Royalty free high-resolution stock photos library."
      },
      getBreadcrumbSchema('Stock Library', '/stock-library')
    ]
  },
  '/privacy-policy': {
    path: '/privacy-policy',
    title: 'Privacy Policy | Smart Imagify',
    description: 'Read our privacy policy to understand how we protect your image data, zero server uploads, and complete device-local processing security.',
    focusKeywords: 'privacy policy, local processing privacy, zero uploads',
    noindex: true,
    schemas: [{ "@type": "WebPage", "name": "Privacy Policy" }]
  },
  '/terms-of-service': {
    path: '/terms-of-service',
    title: 'Terms of Service | Smart Imagify',
    description: 'View the terms of service governing the use of Smart Imagify image compression, AI studio tools, and platform licensing guidelines.',
    focusKeywords: 'terms of service, local license',
    noindex: true,
    schemas: [{ "@type": "WebPage", "name": "Terms of Service" }]
  },
  '/disclaimer': {
    path: '/disclaimer',
    title: 'Disclaimer | Smart Imagify Platform Info',
    description: 'Review our disclaimer outlining user responsibilities, AI usage policies, and liability limitations for locally processed files.',
    focusKeywords: 'disclaimer, liability limitations',
    noindex: true,
    schemas: [{ "@type": "WebPage", "name": "Disclaimer" }]
  },
  '/cookie-policy': {
    path: '/cookie-policy',
    title: 'Cookie Policy | Smart Imagify',
    description: 'Learn about the minimal cookies and local storage used by Smart Imagify for session management and user preferences.',
    focusKeywords: 'cookie policy, local storage, session management',
    noindex: true,
    schemas: [{ "@type": "WebPage", "name": "Cookie Policy" }]
  },
  '/about': {
    path: '/about',
    title: 'About Us | Smart Imagify - Ayshdan Innovations',
    description: 'Discover the story behind Smart Imagify. Created by Muhammad Adnan under AYSHDAN INNOVATIONS to provide high fidelity image utility tools.',
    focusKeywords: 'about, ayshdan innovations, muhammad adnan',
    schemas: [
      { "@type": "WebPage", "name": "About Smart Imagify" },
      getBreadcrumbSchema('About Us', '/about')
    ]
  },
  '/blog': {
    path: '/blog',
    title: 'SEO & Speed Resources Blog | Smart Imagify',
    description: 'Learn how to optimize your web rankings, hit 100/100 PageSpeed scores, and manage Core Web Vitals using smart local conversions.',
    focusKeywords: 'page speed optimization, core web vitals blog, seo tips',
    schemas: [
      { "@type": "WebPage", "name": "Blog Resources" },
      getBreadcrumbSchema('Blog', '/blog')
    ]
  },
  '/contact': {
    path: '/contact',
    title: 'Contact Us | Smart Imagify Support Desk',
    description: 'Get in touch with Muhammad Adnan and the AYSHDAN INNOVATIONS tech support team for queries relative to local SDK, corporate plans, or help.',
    focusKeywords: 'contact support, smart imagify help desk',
    schemas: [
      { "@type": "WebPage", "name": "Contact Support" },
      getBreadcrumbSchema('Contact Us', '/contact')
    ]
  },
  '/background-remover': {
    path: '/background-remover',
    title: 'Background Remover Online | Free AI BG Eraser Tool',
    description: 'Remove image backgrounds instantly using AI. Free online background eraser for product photos, portraits, and ecommerce images.',
    focusKeywords: 'background remover online, remove background from image, ai bg remover',
    schemas: [
      getToolSchema('AI Background Remover (Coming Soon)', 'https://smartimagify.com/background-remover', 'AI background removal offline sandbox tool.'),
      getBreadcrumbSchema('Background Remover', '/background-remover')
    ]
  },
  '/image-upscaler': {
    path: '/image-upscaler',
    title: 'AI Image Upscaler | Enhance Resolution 4x Free Online',
    description: 'Upscale and enhance image resolution using AI. Increase image quality up to 4x without losing detail. Free online image enhancer.',
    focusKeywords: 'ai image upscaler, increase image resolution, enhance image quality',
    schemas: [
      getToolSchema('AI Image Upscaler (Coming Soon)', 'https://smartimagify.com/image-upscaler', 'Super resolution AI image enhancer offline sandbox.'),
      getBreadcrumbSchema('Image Upscaler', '/image-upscaler')
    ]
  },
  '/watermark-remover': {
    path: '/watermark-remover',
    title: 'Watermark Remover Online | Clean Images for Free',
    description: 'Remove watermarks from images online for free. Clean, professional image processing locally in your browser with no uploads.',
    focusKeywords: 'watermark remover online, remove watermark from image',
    schemas: [
      getToolSchema('Watermark Remover (Coming Soon)', 'https://smartimagify.com/watermark-remover', 'Scrub textual or semi-transparent watermark overlays.'),
      getBreadcrumbSchema('Watermark Remover', '/watermark-remover')
    ]
  },
  '/image-to-pdf': {
    path: '/image-to-pdf',
    title: 'Image to PDF Converter | JPG PNG to PDF Online Free',
    description: 'Convert JPG, PNG, and WebP images to PDF online. Combine multiple images into one PDF document. Free, fast, and private.',
    focusKeywords: 'image to pdf, jpg to pdf online, convert image to pdf',
    schemas: [
      getToolSchema('Image to PDF (Coming Soon)', 'https://smartimagify.com/image-to-pdf', 'Combine JPG, PNG, WebP images to highly compressed PDFs.'),
      getBreadcrumbSchema('Image to PDF', '/image-to-pdf')
    ]
  },
  '/pdf-to-image': {
    path: '/pdf-to-image',
    title: 'PDF to Image Converter | Extract Pages Free Online',
    description: 'Convert PDF documents to image formats locally in your browser. Extract individual pages as high-resolution PNG or JPG files for free.',
    focusKeywords: 'pdf to image converter',
    schemas: [
      getToolSchema('PDF to Image (Coming Soon)', 'https://smartimagify.com/pdf-to-image', 'Extract PDF pages as PNG/JPG photos locally in your browser.'),
      getBreadcrumbSchema('PDF to Image', '/pdf-to-image')
    ]
  },
  '/compress-gif': {
    path: '/compress-gif',
    title: 'GIF Compressor | Reduce GIF File Size Online Free',
    description: 'Compress and optimize GIF animations online. Reduce GIF file size without losing quality. Fast local GIF optimization.',
    focusKeywords: 'compress gif online, gif compressor, reduce gif size',
    schemas: [
      getToolSchema('GIF Compressor (Coming Soon)', 'https://smartimagify.com/compress-gif', 'Optimize loop sequences and reduce GIF animations locally.'),
      getBreadcrumbSchema('GIF Compressor', '/compress-gif')
    ]
  },
  '/image-color-picker': {
    path: '/image-color-picker',
    title: 'Image Color Picker | Extract HEX RGB Colors Online',
    description: 'Extract custom HEX, RGB, HSL colors from any photo online. Simple interactive color dropper tool that runs 100% locally.',
    focusKeywords: 'color picker from image',
    schemas: [
      getToolSchema('Image Color Picker (Coming Soon)', 'https://smartimagify.com/image-color-picker', 'Interactive target color picker and scheme builder.'),
      getBreadcrumbSchema('Image Color Picker', '/image-color-picker')
    ]
  },
  '/exif-remover': {
    path: '/exif-remover',
    title: 'EXIF Data Remover | Strip Image Metadata Free',
    description: 'Strip EXIF, GPS, camera metadata and custom tags from your photos locally. Enhance online privacy with one click metadata removal.',
    focusKeywords: 'remove exif data online',
    schemas: [
      getToolSchema('EXIF Data Remover (Coming Soon)', 'https://smartimagify.com/exif-remover', 'Strip EXIF camera metadata, dates, and GPS coordinates locally.'),
      getBreadcrumbSchema('EXIF Data Remover', '/exif-remover')
    ]
  },
  '/dashboard': {
    path: '/dashboard',
    title: 'User Dashboard | Smart Imagify - Smart AI Studio',
    description: 'Access your personal image optimization suite, history log dashboards, and settings variables.',
    focusKeywords: 'user dashboard, image optimizer, smart imagify',
    schemas: [
      { "@type": "WebPage", "name": "Dashboard - Smart Imagify" },
      getBreadcrumbSchema('Dashboard', '/dashboard')
    ]
  },
  '/pricing': {
    path: '/pricing',
    title: 'Affordable Pricing Plans | Smart Imagify Premium Access',
    description: 'Select a suitable premium tier to scale up batch optimizations and premium next-gen dream seed generation capabilities.',
    focusKeywords: 'pricing plans, smart imagify cost, premium subscription',
    schemas: [
      { "@type": "WebPage", "name": "Pricing Plans" },
      getBreadcrumbSchema('Pricing', '/pricing')
    ]
  },
  '/account': {
    path: '/account',
    title: 'My Account | Smart Imagify Profile Settings',
    description: 'Manage user profiles, customize details, check billing dates, and review credit balances.',
    focusKeywords: 'user account, profile settings',
    schemas: [
      { "@type": "WebPage", "name": "User Account" },
      getBreadcrumbSchema('Account', '/account')
    ]
  },
  '/settings': {
    path: '/settings',
    title: 'Platform Settings | Customize Smart Imagify Studio',
    description: 'Adjust default formats, default compressor scales, and customize dark or light mode defaults.',
    focusKeywords: 'platform settings, configurations',
    schemas: [
      { "@type": "WebPage", "name": "Settings" },
      getBreadcrumbSchema('Settings', '/settings')
    ]
  },
  '/referral': {
    path: '/referral',
    title: 'Referral Program | Invite Friends & Earn Free Credits',
    description: 'Invite team members or developers to Smart Imagify and receive free credit tokens instantly on sign-up.',
    focusKeywords: 'referral rewards, free image credits',
    schemas: [
      { "@type": "WebPage", "name": "Referral Rewards" },
      getBreadcrumbSchema('Referral', '/referral')
    ]
  },
  '/history': {
    path: '/history',
    title: 'Compression History Logs | Smart Imagify',
    description: 'Inspect previous image compressions, verify saved size values, and download original backups from local cache.',
    focusKeywords: 'compression logs, saved data cache',
    schemas: [
      { "@type": "WebPage", "name": "Compression History Logs" },
      getBreadcrumbSchema('History', '/history')
    ]
  },
  '/donate': {
    path: '/donate',
    title: 'Support Smart Imagify | Serverless Innovations Donation',
    description: 'Donate and contribute to support maintenance, keeping next-gen client utilities free and open-source.',
    focusKeywords: 'donate support, helper donation',
    schemas: [
      { "@type": "WebPage", "name": "Donate Support" },
      getBreadcrumbSchema('Donate', '/donate')
    ]
  },
  '/faq': {
    path: '/faq',
    title: 'Smart Imagify Help & FAQ | Local Image Optimizer',
    description: 'Find answers on local compression, WebAssembly processing, and privacy guidelines. Get support for your creative workspace.',
    focusKeywords: 'smart imagify faq, local processing help, image compression help',
    schemas: [
      { "@type": "WebPage", "name": "Help & FAQ" },
      getBreadcrumbSchema('FAQ', '/faq')
    ]
  },
  '/tools': {
    path: '/tools',
    title: 'AI Image Studio Tools Directory | Smart Imagify',
    description: 'Explore our catalog of hardware accelerated client-side optimization utilities, image generators, resizers, converters, and creative toolkits.',
    focusKeywords: 'image tools directory, client-side photo utilities, ai editor tools',
    schemas: [
      { "@type": "WebPage", "name": "Tools Directory" },
      getBreadcrumbSchema('Tools', '/tools')
    ]
  },
  '/dashboard-preview': {
    path: '/dashboard-preview',
    title: 'Interactive Dashboard Preview | Smart Imagify creative suite',
    description: 'Preview the futuristic client-side studio dashboard, analytics, real-time compression threads, and workspace layout details.',
    focusKeywords: 'saas dashboard preview, client-side sandbox studio, creative workspace analytics',
    schemas: [
      { "@type": "WebPage", "name": "Dashboard Preview" },
      getBreadcrumbSchema('Dashboard Preview', '/dashboard-preview')
    ]
  },
  '/sitemap': {
    path: '/sitemap',
    title: 'Sitemap | All Smart Imagify Utilities & Models',
    description: 'Access the HTML sitemap directory indexing our 105 next-gen client-side optimization utilities and AI generation models.',
    focusKeywords: 'sitemap, image compressor directory, all tools',
    schemas: [
      { "@type": "WebPage", "name": "Sitemap Directory" },
      getBreadcrumbSchema('Sitemap', '/sitemap')
    ]
  }
};

// Core Helper to inject SEO elements
export function injectSEO(path: string) {
  // Gracefully fallback to homepage if route not found
  const config = SEO_MAP[path] || SEO_MAP['/'];

  // 1. Basic Meta Tags
  document.title = config.title;
  setMeta('description', config.description);
  setMeta('keywords', config.focusKeywords);
  setMeta('author', 'Muhammad Adnan - AYSHDAN INNOVATIONS');
  
  // 2. Canonical Link Tag
  setLink('canonical', `https://smartimagify.com${config.path}`);
  
  // 3. Robots Indexing (legal pages noindex)
  setMeta('robots', config.noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');
  
  // 4. Open Graph Social Metadata
  setOg('og:title', config.title);
  setOg('og:description', config.description);
  setOg('og:url', `https://smartimagify.com${config.path}`);
  setOg('og:image', `https://smartimagify.com/Logo/Favicon.png`); // Main brand image fallback
  setOg('og:image:width', '1200');
  setOg('og:image:height', '630');
  setOg('og:type', 'website');
  setOg('og:site_name', 'Smart Imagify');
  setOg('og:locale', 'en_US');
  
  // 5. Twitter Card Meta Tags
  setMeta('twitter:card', 'summary_large_image');
  setMeta('twitter:title', config.title);
  setMeta('twitter:description', config.description);
  setMeta('twitter:image', `https://smartimagify.com/Logo/Favicon.png`);
  setMeta('twitter:site', '@smartimagify');
  setMeta('twitter:creator', '@ayshdan');

  // 6. JSON-LD Dynamic Schema Markup
  if (config.schemas) {
    injectJsonLd(config.schemas);
  }
}

function setMeta(name: string, content: string) {
  let element = document.querySelector(`meta[name="${name}"]`);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute('name', name);
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);
}

function setOg(property: string, content: string) {
  let element = document.querySelector(`meta[property="${property}"]`);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute('property', property);
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);
}

function setLink(rel: string, href: string) {
  let element = document.querySelector(`link[rel="${rel}"]`);
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', rel);
    document.head.appendChild(element);
  }
  element.setAttribute('href', href);
}

function injectJsonLd(schemas: any[]) {
  // Clear existing schemas first
  const existing = document.querySelectorAll('script[type="application/ld+json"]');
  existing.forEach(el => el.remove());
  
  // Inject new schemas
  schemas.forEach(schema => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);
  });
}
